import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { toast } from "sonner";
import { ArrowLeft, Eye, Bookmark, CalendarClock, Bell, MessageSquare, CheckCircle2, Check, X } from "lucide-react";

export function CandidateActivity({ user, onBack }) {
  const [data, setData] = useState(null);
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");

  const load = () => {
    api.get("/candidate/activity").then((r) => setData(r.data)).catch(() => {});
    api.get("/messages/threads").then((r) => setThreads(r.data.threads)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const open = async (t) => { setActive(t); const { data } = await api.get(`/messages/thread/${t.thread_id}`); setMsgs(data.messages); };
  const send = async () => {
    if (!body.trim()) return;
    try { await api.post("/messages/reply", { thread_id: active.thread_id, body }); setBody(""); open(active); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleAvailability = async (val) => {
    try { await api.put("/candidate/profile", { data: { open_to_offers: val } }); load(); toast.success(val ? "You're now open to offers" : "Availability turned off"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen">
      <header className="ta-glass border-b border-white/10 sticky top-0 z-40">
        <div className="mx-auto max-w-6xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <button onClick={onBack} data-testid="back-to-application" className="inline-flex items-center gap-2 text-sm text-muted hover:text-ink transition-colors duration-200"><ArrowLeft size={16} /> My application</button>
            <NotificationBell />
            <span className="text-sm text-muted hidden sm:block">{user?.name}</span>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 lg:px-8 py-10">
        <h1 className="font-display font-bold text-3xl mb-1">Employer interest</h1>
        <p className="text-sm text-muted mb-8">See who's engaging with your profile and respond to startups.</p>

        {!data ? <p className="text-muted text-sm">Loading…</p> : (
          <>
            {/* Availability toggle */}
            <div className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-surface p-5" data-testid="availability-card">
              <div>
                <p className="font-display font-bold text-ink">Open to offers</p>
                <p className="text-sm text-muted">{data.open_to_offers ? "Employers can see you're actively available." : "Turn this on so employers know you're actively available."}</p>
              </div>
              <button onClick={() => toggleAvailability(!data.open_to_offers)} data-testid="availability-toggle"
                className={`relative h-7 w-12 rounded-full transition-colors duration-200 ${data.open_to_offers ? "bg-emerald" : "bg-white/15"}`}>
                <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all duration-200 ${data.open_to_offers ? "left-6" : "left-1"}`} />
              </button>
            </div>

            {data.availability_stale && (
              <div className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-amber/25 bg-amber/5 px-5 py-3.5" data-testid="availability-nudge">
                <p className="text-sm text-muted">Your availability hasn't been refreshed in a while. Confirm you're still open to offers so you keep appearing in employer searches.</p>
                <button onClick={() => toggleAvailability(true)} data-testid="refresh-availability"
                  className="shrink-0 rounded-full bg-amber px-4 py-2 text-xs font-medium text-black hover:bg-white transition-colors duration-200">Refresh</button>
              </div>
            )}

            {!data.published && (
              <div className="mb-6 rounded-xl border border-amber/20 bg-amber/5 px-4 py-3 text-sm text-amber">
                Your profile is not yet published. Employer interest appears once an admin approves and publishes you (status: {data.status}).
              </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[[Eye, "Profile views", data.views], [Bookmark, "Times saved", data.saves],
                [CalendarClock, "Interview requests", data.interview_requests.length], [Bell, "Notifications", data.notifications.length]].map(([Icon, label, val]) => (
                <div key={label} className="rounded-xl border border-white/10 bg-surface p-5" data-testid={`activity-stat-${label}`}>
                  <Icon size={16} className="text-azure mb-2" />
                  <p className="font-display font-bold text-3xl text-ink">{val}</p>
                  <p className="label-mono text-faint mt-1">{label}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="rounded-2xl border border-white/10 bg-surface p-5">
                  <p className="label-mono text-azure mb-3">Interview requests</p>
                  {data.interview_requests.length === 0 ? <p className="text-sm text-muted">No interview requests yet.</p> :
                    data.interview_requests.map((r) => <InterviewCard key={r.id} r={r} onDone={load} />)}
                </div>
                <div className="rounded-2xl border border-white/10 bg-surface p-5">
                  <p className="label-mono text-azure mb-3">Notifications</p>
                  {data.notifications.length === 0 ? <p className="text-sm text-muted">Nothing yet.</p> : data.notifications.map((n) => (
                    <div key={n.id} className="flex items-start gap-2 py-2 border-b border-white/5 text-sm text-muted">
                      <CheckCircle2 size={14} className="text-emerald mt-0.5 shrink-0" /> {n.message}
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-surface p-4">
                <p className="label-mono text-azure mb-3 flex items-center gap-2"><MessageSquare size={14} /> Messages</p>
                {!active ? (
                  threads.length === 0 ? <p className="text-sm text-muted">No conversations yet.</p> :
                    <div className="space-y-1">{threads.map((t) => (
                      <button key={t.thread_id} onClick={() => open(t)} className="w-full text-left rounded-lg p-3 hover:bg-white/5 transition-colors duration-200">
                        <p className="text-sm text-ink font-medium">{t.company_name}</p>
                        <p className="text-xs text-faint truncate">{t.last_body}</p>
                      </button>
                    ))}</div>
                ) : (
                  <div className="flex flex-col min-h-[300px]">
                    <button onClick={() => setActive(null)} className="text-xs text-azure mb-2 self-start">← All conversations</button>
                    <div className="flex-1 space-y-2 overflow-y-auto">
                      {msgs.map((m) => (
                        <div key={m.id} className={`max-w-[75%] rounded-xl px-3 py-2 text-sm ${m.sender === "candidate" ? "ml-auto bg-azure/15 text-ink" : "bg-white/5 text-muted"}`}>{m.body}</div>
                      ))}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply…" data-testid="candidate-reply-input"
                        className="flex-1 rounded-lg border border-white/12 bg-base px-3 py-2 text-sm text-ink focus:border-azure focus:outline-none" />
                      <button onClick={send} data-testid="candidate-reply-send" className="rounded-full bg-azure px-4 py-2 text-sm text-white hover:bg-white hover:text-black transition-colors duration-200">Send</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InterviewCard({ r, onDone }) {
  const [times, setTimes] = useState("");
  const [proposing, setProposing] = useState(false);
  const respond = async (action) => {
    try { await api.put(`/candidate/interview-requests/${r.id}`, { action, proposed_times: action === "accept" ? times : null }); toast.success(`Interview ${action === "accept" ? "accepted" : "declined"}`); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const done = r.status === "accepted" || r.status === "declined";
  return (
    <div className="rounded-lg border border-white/10 p-3 mb-2" data-testid={`interview-req-${r.id}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink font-medium">{r.company_name}</p>
        <span className={`text-[10px] font-mono ${r.status === "accepted" ? "text-emerald" : r.status === "declined" ? "text-signal" : "text-amber"}`}>{(r.status || "").replace(/_/g, " ")}</span>
      </div>
      <p className="text-xs text-muted mt-0.5">{r.message}</p>
      {r.candidate_proposed_times && <p className="text-[11px] text-azure font-mono mt-1">Your proposed times: {r.candidate_proposed_times}</p>}
      {!done && (
        <div className="mt-3">
          {proposing && (
            <input value={times} onChange={(e) => setTimes(e.target.value)} placeholder="e.g. Tue 2pm ET, Wed 10am ET" data-testid={`propose-times-${r.id}`}
              className="mb-2 w-full rounded-lg border border-white/12 bg-base px-3 py-1.5 text-xs text-ink focus:border-azure focus:outline-none" />
          )}
          <div className="flex gap-2">
            {!proposing ? (
              <button onClick={() => setProposing(true)} data-testid={`accept-interview-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald px-3 py-1.5 text-xs font-medium text-black hover:bg-white transition-colors duration-200"><Check size={13} /> Accept & propose times</button>
            ) : (
              <button onClick={() => respond("accept")} data-testid={`confirm-accept-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald px-3 py-1.5 text-xs font-medium text-black hover:bg-white transition-colors duration-200"><Check size={13} /> Confirm accept</button>
            )}
            <button onClick={() => respond("decline")} data-testid={`decline-interview-${r.id}`} className="inline-flex items-center gap-1 rounded-full border border-signal/30 text-signal px-3 py-1.5 text-xs hover:bg-signal/10 transition-colors duration-200"><X size={13} /> Decline</button>
          </div>
        </div>
      )}
    </div>
  );
}
